import { useEffect, useRef, useState } from "react";
import { Play, Pause, Volume2 } from "lucide-react";

const FrequencyPlayer = ({ frequencies }) => {
  const [playing, setPlaying] = useState(null); // hz currently playing
  const ctxRef = useRef(null);
  const oscRef = useRef(null);
  const gainRef = useRef(null);

  const stop = () => {
    if (oscRef.current) {
      try { oscRef.current.stop(); } catch { /* noop */ }
      oscRef.current.disconnect();
      oscRef.current = null;
    }
    setPlaying(null);
  };

  const play = (hz) => {
    if (playing === hz) { stop(); return; }
    stop();
    if (!ctxRef.current) ctxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    const ctx = ctxRef.current;
    if (ctx.state === "suspended") ctx.resume();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.value = hz;
    gain.gain.setValueAtTime(0, ctx.currentTime);
    gain.gain.linearRampToValueAtTime(0.18, ctx.currentTime + 0.4); // gentle fade-in
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    oscRef.current = osc;
    gainRef.current = gain;
    setPlaying(hz);
  };

  useEffect(() => () => stop(), []); // cleanup on unmount

  return (
    <div className="space-y-2" data-testid="frequency-player">
      <p className="text-xs text-ink-mute flex items-center gap-1.5"><Volume2 className="w-3.5 h-3.5" /> Start at a low volume. Headphones deepen the effect. Tap again to stop.</p>
      {frequencies.map((f) => (
        <button key={f.hz} onClick={() => play(f.hz)} data-testid={`freq-${f.hz}`}
          className={`w-full flex items-center gap-3 rounded-2xl border px-4 py-3 text-left transition-colors ${playing === f.hz ? "bg-brand text-linen border-brand" : "bg-white border-linen-3 hover:bg-linen-2"}`}>
          <span className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${playing === f.hz ? "bg-linen/20" : "bg-calm"}`}>
            {playing === f.hz ? <Pause className="w-4 h-4" /> : <Play className={`w-4 h-4 ${playing === f.hz ? "text-linen" : "text-brand"}`} />}
          </span>
          <span className="flex-1 min-w-0">
            <span className={`font-medium ${playing === f.hz ? "text-linen" : "text-ink"}`}>{f.name}</span>
            <span className={`block text-sm ${playing === f.hz ? "text-linen/85" : "text-ink-2"}`}>{f.purpose}</span>
          </span>
          {playing === f.hz && <span className="text-xs text-linen/90 animate-pulse">playing…</span>}
        </button>
      ))}
    </div>
  );
};

export default FrequencyPlayer;
